import { CssInlineStyles, CssInlineStylesAdapter } from '@fmnts/api';
import { FormType } from '@fmnts/api/shared';
import { Adapter } from '@fmnts/core';
import { Md5 } from 'ts-md5';
import { ApiPresentationAdapter } from '../adapter/presentation.adapter';
import { ApiRequirementAdapter } from '../adapter/requirement.adapter';
import {
  IApiCampaign,
  IApiRaiseNowCampaignConfig,
} from '../api-model/campaign.api';
import { CampaignPage, CampaignPageAdapter } from './campaign-page';
import { NextFormMinimal } from './next-form';
import { Presentation } from './presentation';
import { Requirement } from './requirement';

export class Campaign {
  constructor(
    public id: string,
    public logo: string,
    public name: string,
    public raisenow: IApiRaiseNowCampaignConfig,
    public pages: CampaignPage[],
    /** Presentation or null if no presentation is assigned. */
    public presentation: Presentation | null,
    public campaignLogo: string,
    public useNextForm: boolean,
    public supportedLanguages: Record<string, string>,
    public style: string,
    /** additional inline css styles to apply to the campaign form */
    public inlineStyles: CssInlineStyles,
    /**
     * Hash that can be used to check for changes when comparing
     * campaign instances
     */
    public readonly versionHash: string,
    /** Requirements for this campaign */
    public readonly requirements: readonly Requirement[],
  ) {}
}

export class CampaignAdapter implements Adapter<Campaign> {
  constructor(
    private pageAdapter = new CampaignPageAdapter(),
    private presentationAdapter = new ApiPresentationAdapter(),
    private cssinlineStylesAdapter = new CssInlineStylesAdapter(),
    private requirementAdapter = new ApiRequirementAdapter(),
  ) {}

  adapt(item: IApiCampaign): Campaign {
    const versionHash = Md5.hashStr(JSON.stringify(item));
    const requirements = (item.requirements ?? [])
      .filter((requirement) => this.requirementAdapter.canAdapt(requirement))
      .map((req) => this.requirementAdapter.adapt(req));

    return new Campaign(
      item.id,
      item.logo,
      item.name,
      item.raisenow_config,
      (item.pages || []).map((page) => this.pageAdapter.adapt(page)),
      item.presentation
        ? this.presentationAdapter.adapt(item.presentation)
        : null,
      item.organization_logo,
      item.use_next_form,
      item.supported_languages,
      item.style,
      this.cssinlineStylesAdapter.adapt(item),
      versionHash,
      requirements,
    );
  }
}

/**
 * Helper function to convert a campaign to a next minimal form.
 *
 * @param campaign
 * @returns
 */
export function campaignToNextForm(campaign: Campaign): NextFormMinimal {
  return {
    formType: FormType.Donation,
    id: campaign.id,
    logo: campaign.campaignLogo,
    name: campaign.name,
    organizationLogo: campaign.logo,
    requirements: campaign.requirements,
    supportedLanguages: campaign.supportedLanguages,
    useNextForm: campaign.useNextForm,
    modifytime: new Date().toISOString(),
  };
}
